import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { UseMutateAsyncFunction } from '@tanstack/react-query';
import axios from 'axios';

import { EVENTS, getDocumentsSignedProperties, useIdentify, useTrack } from '@arrived/analytics';
import {
  canOfferingStatusReserveShares,
  isMissingEntityTitle,
  getStringFromError,
  isOfferingForSale,
  notificationService,
  shouldUseSharesAvailableEndpoint,
} from '@arrived/common';
import { trackMarketingConversionEvent } from '@arrived/marketing';
import {
  CreateOfferingTransactionRequest,
  CreateOfferingTransactionType,
  FundType,
  MARKETING_CONVERSION_EVENTS,
  Offering,
  OfferingId,
  OfferingTransaction,
  Rewards,
  SecurityType,
  SignedStatus,
  TradePromotionDetails,
  TRANSACTION_ERROR,
} from '@arrived/models';
import {
  QueryError,
  useDeleteTradeMutation,
  useGetCurrentUserQuery,
  useGetPrimaryAccountQuery,
  useGetUserSsnQuery,
  useIsOfferingOwned,
  useIsOfferingTrending,
  useMarketROIQuery,
  useOfferingDividendsQuery,
  useOfferingQuery,
  usePostPromotionRedemptionPreviewMutation,
  usePostSignTradeMutation,
  usePostTradeMutation,
  useSharesAvailableQuery,
  useTradeQuery,
} from '@arrived/queries';

import { ShareSelectionContext } from './ShareSelectionContext';
import { TransactFlowContext } from './TransactFlowContext';

export interface Discount {
  id: string;
  name: string;
  code?: string | null;
  value: number;
}

export interface TransactContextState {
  appliedPromotions: TradePromotionDetails[];
  setAppliedPromotions: (promotions: TradePromotionDetails[]) => void;
  appliedVouchers: Rewards.Voucher[];
  setAppliedVouchers: (vouchers: Rewards.Voucher[]) => void;
  availableVouchers: Rewards.Voucher[];
  setAvailableVouchers: (availableVouchers: Rewards.Voucher[]) => void;
  totalCost: number;
  setTotalCost: (totalCost: number) => void;
  previewPromo: UseMutateAsyncFunction<
    Rewards.RewardRedemption | undefined,
    QueryError | undefined,
    Omit<Rewards.RewardRedemptionRequest, 'accountUuid' | 'promotionUuid'>,
    undefined
  >;
  isPromoLoading?: boolean;
  discount?: Discount;
  setDiscount: (discount?: Discount) => void;
  isBuyingTermsAgreed: boolean;
  setIsBuyingTermsAgreed: (isBuyingTermsAgreed: boolean) => void;
  isLoadingShareData: boolean;
  isPrimaryButtonDisabled: boolean;
  setIsPrimaryButtonDisabled: (isPrimaryButtonDisabled: boolean) => void;
  isTransactionSubmitting: boolean;
  isTransactionCancelling: boolean;
  setIsTransactionSubmitting: (isTransactionSubmitting: boolean) => void;
  transactShares: () => Promise<OfferingTransaction | void>;
  deleteTrade: (id: number) => Promise<OfferingTransaction>;
  signature: string;
  setSignature: (signature: string) => void;
  isMinimumHoldingPeriodAccepted: boolean | undefined;
  setIsMinimumHoldingPeriodAccepted: (acceptMinHoldPeriod: boolean) => void;
  signDocuments: () => Promise<OfferingTransaction | void>;
  offeringId?: OfferingId;
  pageContext: TRANSACT_PAGE_CONTEXT;
  step: TRANSACT_STEP;
  missingData?: boolean;
  invalidState?: TRANSACT_INVALID_STATE;
  transactVoucherErrors: string[];
  setTransactVoucherErrors: (errors: string[]) => void;
}

export const TransactContext = createContext({} as TransactContextState);

export enum TRANSACT_PAGE_CONTEXT {
  PROPERTY_INLINE,
  SINGLE_TRANSACTION,
}

export enum TRANSACT_STEP {
  SELECT_SHARES,
  MISSING_DATA,
  TRADE_DETAILS,
  SIGN_DOCUMENTS,
  SUCCESS,
}

export enum TRANSACT_INVALID_STATE {
  NO_PERMISSIONS,
  DOES_NOT_EXIST,
}
interface TransactContextProviderProps {
  offeringId: OfferingId;
  tradeId?: number;
  pageContext: TRANSACT_PAGE_CONTEXT;
  stepOverride?: TRANSACT_STEP;
  duplicateTradeRedirect: () => void;
  children?: ReactNode;
}

export const TransactContextProvider = ({
  children,
  offeringId,
  tradeId,
  stepOverride,
  pageContext = TRANSACT_PAGE_CONTEXT.PROPERTY_INLINE,
  duplicateTradeRedirect,
}: TransactContextProviderProps) => {
  const track = useTrack();
  const identify = useIdentify();
  const offeringState = useOfferingQuery(offeringId);
  const currentUserState = useGetCurrentUserQuery();
  const accountState = useGetPrimaryAccountQuery();
  const userSsnState = useGetUserSsnQuery(currentUserState.data?.id);
  const marketROIState = useMarketROIQuery(offeringState.data?.market?.id);
  const offeringDividendsState = useOfferingDividendsQuery(offeringId);
  const { mutateAsync: postTrade } = usePostTradeMutation({
    onError: (error) => {
      if (
        error &&
        error.response?.status === 422 &&
        error.response?.data.error.error === TRANSACTION_ERROR.UNPROCESSABLE_ENTITY
      ) {
        notificationService.success(
          <FormattedMessage
            id="transact-shares-duplicate-trade-message"
            defaultMessage="You have another trade matching the same criteria, you can find it here!"
          />
        );

        duplicateTradeRedirect();
      }
    },
  });
  const { mutateAsync: postSignTrade } = usePostSignTradeMutation();
  const isSharesAvailableEnabled = offeringState.data && shouldUseSharesAvailableEndpoint(offeringState.data.status);
  const sharesAvailableState = useSharesAvailableQuery(offeringId, { enabled: isSharesAvailableEnabled });

  const isOfferingTrending = useIsOfferingTrending();
  const isOfferingOwned = useIsOfferingOwned();
  const { setTradeInProgress } = useContext(TransactFlowContext);
  const tradeState = useTradeQuery(tradeId);

  const { maxShares, minShares, sharesToBuy, subtotal } = useContext(ShareSelectionContext);

  const { mutateAsync: deleteTrade, isLoading: isTransactionCancelling } = useDeleteTradeMutation();
  const [appliedPromotions, setAppliedPromotions] = useState<TradePromotionDetails[]>([]);
  const [appliedVouchers, setAppliedVouchers] = useState<Rewards.Voucher[]>([]);
  const [availableVouchers, setAvailableVouchers] = useState<Rewards.Voucher[]>([]);
  const [discount, setDiscount] = useState<Discount>();
  const [totalCost, setTotalCost] = useState<number>(subtotal);
  const [isBuyingTermsAgreed, setIsBuyingTermsAgreed] = useState(false);
  const [isPrimaryButtonDisabled, setIsPrimaryButtonDisabled] = useState(true);
  const [isTransactionSubmitting, setIsTransactionSubmitting] = useState(false);
  const [signature, setSignature] = useState<string>('');
  const [isMinimumHoldingPeriodAccepted, setIsMinimumHoldingPeriodAccepted] = useState<boolean>();
  const [invalidState, setInvalidState] = useState<TRANSACT_INVALID_STATE>();
  const [transactVoucherErrors, setTransactVoucherErrors] = useState<string[]>([]);
  const { mutateAsync: previewPromo, isLoading: isPromoLoading } = usePostPromotionRedemptionPreviewMutation();

  const { data: accountStateData } = accountState;
  const { data: currentUserStateData } = currentUserState;

  useEffect(() => {
    if (!appliedPromotions.length) {
      setTotalCost(subtotal);
    }
  }, [appliedPromotions, subtotal]);

  useEffect(() => {
    if (tradeState.isSuccess) {
      setTradeInProgress(tradeState.data);
    }

    if (tradeState.isError) {
      const status = tradeState.error?.response?.status;
      if (status === 401) {
        setInvalidState(TRANSACT_INVALID_STATE.NO_PERMISSIONS);
      }

      if (status === 404) {
        setInvalidState(TRANSACT_INVALID_STATE.DOES_NOT_EXIST);
      }
    }
  }, [tradeState]);

  const missingData = useMemo(() => {
    return (accountState.isSuccess && isMissingEntityTitle(accountState.data)) || userSsnState.isError;
  }, [accountState, userSsnState]);

  const step = useMemo(() => {
    if (stepOverride !== undefined) {
      return stepOverride;
    }

    if (tradeState.isSuccess) {
      if (tradeState.data.documentsSignedStatus === SignedStatus.SIGNED) return TRANSACT_STEP.SUCCESS;
      if (missingData) return TRANSACT_STEP.MISSING_DATA;
      return TRANSACT_STEP.SIGN_DOCUMENTS;
    }

    return TRANSACT_STEP.SELECT_SHARES;
  }, [tradeState, stepOverride, missingData]);

  const { status } = offeringState.data || {};
  const isReserve = canOfferingStatusReserveShares(status);

  useEffect(() => {
    const isDisabled = sharesToBuy === 0 || isTransactionSubmitting;
    const validAmount = sharesToBuy >= minShares && sharesToBuy <= maxShares;
    const isTermsAgreed = isReserve || isBuyingTermsAgreed;
    const areVoucherErrors = transactVoucherErrors.length > 0;
    setIsPrimaryButtonDisabled(!validAmount || isDisabled || !isTermsAgreed || areVoucherErrors);
  }, [
    isBuyingTermsAgreed,
    isReserve,
    isTransactionSubmitting,
    sharesToBuy,
    minShares,
    maxShares,
    transactVoucherErrors,
  ]);

  const transactShares = async (): Promise<OfferingTransaction | void> => {
    const request = buildTransactRequest();

    if (request) {
      setIsTransactionSubmitting(true);
      const offeringTransaction = await postTrade(request).catch((err) => {
        if (axios.isAxiosError(err)) {
          if (err?.response?.data.error.error === TRANSACTION_ERROR.REWARDS_SERVICE) {
            notificationService.error(
              <FormattedMessage
                id="rewards-error"
                defaultMessage="There was an error with your promotion. The promotion may not be active anymore."
              />
            );
          }
        }
      });
      setIsTransactionSubmitting(false);

      if (offeringTransaction) setTradeInProgress(offeringTransaction);
      return offeringTransaction;
    } else {
      throw new Error('Error building transaction request.');
    }
  };

  const signDocuments = async (): Promise<OfferingTransaction | void> => {
    if (!tradeState.data) return;
    const offeringTransaction = tradeState.data;

    setIsTransactionSubmitting(true);

    if (!signature) {
      notificationService.error(
        <FormattedMessage
          id="trade-sign-documents-error-create-digital-signature"
          defaultMessage="Please create your digital signature first."
        />
      );
      return;
    }

    if (offeringState.data?.securityType === SecurityType.REG_D && !isMinimumHoldingPeriodAccepted) {
      notificationService.error(
        <FormattedMessage id="accept-min-hold-period" defaultMessage="Must accept minimum holding period" />
      );
      return;
    }

    const { id: offeringTransactionId } = offeringTransaction;
    return await postSignTrade({
      signature,
      offeringTransactionId,
      acceptMinHoldPeriod: isMinimumHoldingPeriodAccepted,
    })
      .then((offeringTransaction) => {
        setTradeInProgress(offeringTransaction);
        if (offeringState.isSuccess) {
          const documentsSignedProperties = getDocumentsSignedProperties(
            offeringState.data,
            offeringTransaction,
            offeringDividendsState.data,
            marketROIState.data,
            {
              signing_completion_location: 'single transaction page',
              property_tag_status: getTags(offeringState.data).join(','),
            }
          );

          track(EVENTS.DOCUMENTS_SIGNED, documentsSignedProperties);
          identify({
            has_had_first_investment_made: true,
          });

          trackMarketingConversionEvent({
            type: MARKETING_CONVERSION_EVENTS.TRADE_DOCS_SIGNED,
            value: documentsSignedProperties?.revenue,
          });
        }

        return offeringTransaction;
      })
      .catch((error) => {
        notificationService.error(
          <FormattedMessage
            id="sign-documents-error"
            defaultMessage={'Error signing documents: {error}'}
            values={{ error: getStringFromError(error) }}
          />
        );
      })
      .finally(() => {
        setIsTransactionSubmitting(false);
      });
  };

  const getTags = (offering: Offering) => {
    const tags = [];
    if (isOfferingOwned(offering.id)) tags.push('owned');
    if (isOfferingTrending(offering.id)) tags.push('trending');
    if (isOfferingForSale(offering)) tags.push('forsale');
    return tags;
  };

  const buildTransactRequest = () => {
    const { COMMIT, RESERVE, TRANSACT } = CreateOfferingTransactionType;

    if (offeringState.isSuccess) {
      const { id } = offeringState.data;

      const tags = getTags(offeringState.data);

      const request: CreateOfferingTransactionRequest = {
        fundType: FundType.ACH,
        offeringId: id,
        sharesCount: sharesToBuy,
        type: isReserve ? RESERVE : COMMIT,
        userId: currentUserStateData?.id,
        tags,
        // Made to handle multiple promotions if we decide to do so.
        promotion: appliedPromotions.length ? appliedPromotions[0] : undefined,
        voucherUuids: appliedVouchers.length ? appliedVouchers.map((voucher) => voucher.uuid) : undefined,
      };

      if (accountStateData) {
        if (!isReserve) {
          request.type = TRANSACT;
        }

        request.accountId = accountStateData.id;

        delete request.userId;
      }

      return request;
    }

    return;
  };

  const isLoadingShareData = sharesAvailableState.isLoading || offeringState.isLoading;

  const values = {
    totalCost,
    setTotalCost,

    appliedPromotions,
    setAppliedPromotions,

    appliedVouchers,
    setAppliedVouchers,

    availableVouchers,
    setAvailableVouchers,

    transactVoucherErrors,
    setTransactVoucherErrors,

    discount,
    setDiscount,

    previewPromo,
    isPromoLoading,

    isBuyingTermsAgreed,
    setIsBuyingTermsAgreed,

    isLoadingShareData,

    isPrimaryButtonDisabled: isPrimaryButtonDisabled || isPromoLoading,
    setIsPrimaryButtonDisabled,

    isTransactionSubmitting,
    isTransactionCancelling,
    setIsTransactionSubmitting,
    transactShares,
    deleteTrade,

    isReserve,

    signature,
    setSignature,

    isMinimumHoldingPeriodAccepted,
    setIsMinimumHoldingPeriodAccepted,

    signDocuments,
    offeringId,
    step,
    pageContext,
    missingData,
    invalidState,
  };

  const value = useMemo(() => values, Object.values(values));

  return <TransactContext.Provider value={value}>{children}</TransactContext.Provider>;
};
